import React, { useState, useEffect, useCallback } from "react";
import { apiGet } from "../../services/api";
import { safeDate } from "../../utils/dateUtils";
import Button from "../common/Button";

interface PrescriptionMedicine {
  drug_id?: number | string | null;
  drug_name?: string;
  days?: number;
  frequency?: string;
  instructions?: string;
  [k: string]: unknown;
}

interface PrescriptionRx {
  id?: number;
  date?: string;
  medicines: PrescriptionMedicine[] | string;
  [k: string]: unknown;
}

export interface PrescriptionLoaderProps {
  patient: { patient_id: string; [k: string]: unknown } | null;
  onLoadPrescription?: (medicines: PrescriptionMedicine[]) => void;
}

export default function PrescriptionLoader({ patient, onLoadPrescription }: PrescriptionLoaderProps) {
  const [prescriptions, setPrescriptions] = useState<PrescriptionRx[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRx, setSelectedRx] = useState<PrescriptionRx | null>(null);
  const [editableRx, setEditableRx] = useState<(PrescriptionRx & { medicines: PrescriptionMedicine[] }) | null>(null);

  const loadPrescriptions = useCallback(async () => {
    if (!patient?.patient_id) return;
    setLoading(true);
    try {
      const rxs = await apiGet(`/api/pharmacy/prescriptions/patient/${patient.patient_id}`);
      setPrescriptions(Array.isArray(rxs) ? (rxs as PrescriptionRx[]) : []);
    } catch (e) {
      console.error("Failed to load prescriptions", e);
      setPrescriptions([]);
    } finally {
      setLoading(false);
    }
  }, [patient?.patient_id]);

  useEffect(() => {
    if (patient?.patient_id) {
      loadPrescriptions();
    } else {
      setPrescriptions([]);
    }
  }, [patient, loadPrescriptions]);

  const handleLoad = (rx: PrescriptionRx) => {
    try {
      const medicines: PrescriptionMedicine[] =
        typeof rx.medicines === "string" ? JSON.parse(rx.medicines) : (rx.medicines ?? []);
      setEditableRx({ ...rx, medicines });
      setSelectedRx(rx);
    } catch (e) {
      console.error("Failed to parse prescription", e);
    }
  };

  const handleEditDrug = (index: number, field: string, value: string | number) => {
    setEditableRx((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        medicines: prev.medicines.map((drug, i) =>
          i === index ? { ...drug, [field]: value } : drug
        ),
      };
    });
  };

  const handleAddDrug = () => {
    setEditableRx((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        medicines: [
          ...prev.medicines,
          { drug_id: null, drug_name: "", days: 1, frequency: "1-0-1", instructions: "" },
        ],
      };
    });
  };

  const handleRemoveDrug = (index: number) => {
    setEditableRx((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        medicines: prev.medicines.filter((_, i) => i !== index),
      };
    });
  };

  const handleApply = () => {
    if (editableRx && onLoadPrescription) {
      onLoadPrescription(editableRx.medicines);
      setSelectedRx(null);
      setEditableRx(null);
    }
  };

  if (!patient) {
    return null;
  }

  if (selectedRx && editableRx) {
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => { setSelectedRx(null); setEditableRx(null); }}>
        <div className="mod-card" style={{ maxWidth: '800px', width: '100%', padding: '0', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }} onClick={(e) => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
            <h4 style={{ margin: 0, color: '#0f172a', fontSize: '1.2rem', display: 'flex', alignItems: 'center', gap: '8px' }}>📋 Edit Prescription</h4>
            <Button variant="ghost" onClick={() => { setSelectedRx(null); setEditableRx(null); }} style={{ fontSize: '1.5rem', padding: '0 8px' }}>✕</Button>
          </div>
          <div style={{ padding: '24px', maxHeight: '60vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {editableRx.medicines.map((drug, index) => (
                <div key={index} style={{ display: 'flex', gap: '12px', alignItems: 'center', padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <input
                    type="text"
                    placeholder="Drug Name"
                    value={drug.drug_name ?? ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleEditDrug(index, "drug_name", e.target.value)
                    }
                    className="mod-input"
                    style={{ flex: 2, margin: 0 }}
                  />
                  <input
                    type="number"
                    placeholder="Days"
                    value={drug.days ?? 1}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleEditDrug(index, "days", parseInt(e.target.value, 10) || 1)
                    }
                    className="mod-input"
                    style={{ flex: 1, margin: 0 }}
                  />
                  <input
                    type="text"
                    placeholder="Frequency (e.g., 1-0-1)"
                    value={drug.frequency ?? ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleEditDrug(index, "frequency", e.target.value)
                    }
                    className="mod-input"
                    style={{ flex: 1.5, margin: 0 }}
                  />
                  <Button variant="ghost" onClick={() => handleRemoveDrug(index)} style={{ color: '#dc2626', padding: '8px' }}>
                    ✕
                  </Button>
                </div>
              ))}
              <div style={{ marginTop: '8px' }}>
                <Button variant="outline" onClick={handleAddDrug} size="sm">
                  + Add Drug
                </Button>
              </div>
            </div>
          </div>
          <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px', background: '#f8fafc' }}>
            <Button variant="secondary" onClick={() => { setSelectedRx(null); setEditableRx(null); }}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleApply}>
              Apply to Cart
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Button
        variant="secondary"
        onClick={loadPrescriptions}
        disabled={loading || !patient}
        isLoading={loading}
        style={{ width: '100%', justifyContent: 'center' }}
      >
        📋 Load Prescription
      </Button>
      {prescriptions.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '250px', overflowY: 'auto', paddingRight: '4px' }}>
          {prescriptions.map((rx, idx) => (
            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#0f172a' }}>Prescription #{rx.id ?? idx + 1}</span>
                <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{safeDate(rx.date)?.toLocaleDateString() ?? "—"}</span>
              </div>
              <Button variant="outline" size="sm" onClick={() => handleLoad(rx)}>
                Load
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
